import React, { useState, useRef, useEffect } from "react";
import { Info, Download, Upload, CheckCircle, X, Loader2 } from "lucide-react";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../contexts/AuthContext";
import AddInterventionPointsInfo from "./AddIntervenvtionPointInfoModal";
function AddInterventionPoints() {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadFilename, setDownloadFilename] = useState("");
  const [progressMessage, setProgressMessage] = useState("");
  const [showValidationGuide, setShowValidationGuide] = useState(false);
  const fileInputRef = useRef(null);
  const { fetchWithAuth } = useAuth();

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 20000);
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && showValidationGuide) {
        setShowValidationGuide(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showValidationGuide]);

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        window.URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  const handleFileSelect = (selectedFile) => {
    if (
      selectedFile &&
      selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      if (downloadUrl) {
        window.URL.revokeObjectURL(downloadUrl);
        setDownloadUrl(null);
        setDownloadFilename("");
      }
      setFile(selectedFile);
      setUploadResult(null); // Clear previous results
      showMessage("File selected successfully", "success");
    } else {
      showMessage("Please select a valid Excel file (.xlsx)", "error");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    handleFileSelect(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      showMessage("Please select a file first", "error");
      return;
    }

    if (downloadUrl) {
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
      setDownloadFilename("");
    }

    setIsUploading(true);
    setUploadProgress(0);
    setProgressMessage("");
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetchWithAuth(
        `/intervention-points/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("Streaming response not supported by this browser.");
        }

        const decoder = new TextDecoder();
        let buffer = "";
        let finalResult = null;
        let streamError = null;

        const processEvent = (event) => {
          const status = event.status || "";

          if (status === "started") {
            setProgressMessage(event.message || "Starting upload...");
            setUploadProgress(5);
          } else if (status === "progress") {
            const percentage = Math.min(
              100,
              Math.max(0, Number(event.percentage) || 0)
            );
            setUploadProgress(percentage);
            setProgressMessage(event.message || "Processing rows...");
          } else if (status === "complete") {
            finalResult = event.result || null;
            setUploadProgress(100);
            setProgressMessage(event.message || "Upload complete");
          } else if (status === "error") {
            streamError =
              event.message || "Upload failed during server processing.";
            setProgressMessage("");
          }
        };

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              const event = JSON.parse(line);
              processEvent(event);
              if (event.status === "error") {
                break;
              }
            } catch (parseError) {
              console.error("Failed to parse stream chunk:", parseError);
            }
          }

          if (streamError) {
            break;
          }
        }

        if (buffer.trim() && !streamError) {
          try {
            const event = JSON.parse(buffer);
            processEvent(event);
          } catch (parseError) {
            console.error("Failed to parse final stream chunk:", parseError);
          }
        }

        if (streamError) {
          showMessage(streamError, "error");
        } else if (finalResult) {
          const { file: fileInfo, ...summary } = finalResult;
          setUploadResult(summary);

          if (fileInfo && fileInfo.content) {
            try {
              const byteCharacters = atob(fileInfo.content);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i += 1) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const blob = new Blob([new Uint8Array(byteNumbers)], {
                type:
                  fileInfo.mime_type ||
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
              const url = window.URL.createObjectURL(blob);
              setDownloadUrl(url);
              setDownloadFilename(
                fileInfo.filename || "intervention_points_upload_report.xlsx"
              );
            } catch (decodeError) {
              console.error("Failed to prepare download file:", decodeError);
            }
          }

          const defaultSummary = `Processed ${
            summary.total_processed ?? summary.created ?? 0
          } rows: ${summary.created || 0} added, ${
            summary.skipped || 0
          } skipped.`;
          const successMessage = summary.summary || defaultSummary;

          if (summary.total_errors > 0) {
            showMessage(
              `${successMessage} ${summary.total_errors} errors encountered.`,
              "warning"
            );
          } else {
            showMessage(successMessage, "success");
          }

          setFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        } else {
          showMessage(
            "Upload completed without a final response from the server.",
            "error"
          );
        }
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || "Upload failed", "error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      showMessage("Upload failed. Please try again.", "error");
    } finally {
      setIsUploading(false);
      setTimeout(() => {
        setUploadProgress(0);
        setProgressMessage("");
      }, 500);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetchWithAuth(
        `/intervention-points/template`
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "intervention_points_template.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        showMessage("Failed to download template", "error");
      }
    } catch (error) {
      console.error("Error downloading template:", error);
      showMessage("Failed to download template", "error");
    }
  };

  const downloadResults = () => {
    if (downloadUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        downloadFilename || "intervention_points_upload_report.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
      setDownloadFilename("");
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => window.history.back()}
            className="mr-4 mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Add Intervention Points
          </h1>
          <button
            onClick={() => setShowValidationGuide(true)}
            className="w-10 h-10 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center transition-colors duration-200"
            title="Data Validation Guide"
          >
            <Info className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Template columns
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
              <thead className="bg-gray-50 dark:bg-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Column Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Required
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    intervention_point_name
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    The intervention point name (informative only)
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    No, but recommended
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    latitude
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    The latitude of the intervention point
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      Yes
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    longitude
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    The longitude of the intervention point
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      Yes
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    shop_sign_id
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    The shop sign ID of the intervention point
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    No, but recommended
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-6">
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md transition-colors"
          >
            <Download className="w-5 h-5 mr-2" />
            Download Template
          </button>
        </div>

        <div className="mb-6">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="space-y-4">
              <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <div>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {file ? file.name : "Upload Excel file"}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {file
                    ? "File selected successfully"
                    : "Drag and drop an Excel file here, or click to browse"}
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileInputChange}
                className="hidden"
              />
              {!file && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
                >
                  Browse Files
                </button>
              )}
            </div>
          </div>
        </div>

        {file && (
          <div className="mb-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {file.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                  if (downloadUrl) {
                    window.URL.revokeObjectURL(downloadUrl);
                    setDownloadUrl(null);
                    setDownloadFilename("");
                  }
                }}
                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {isUploading && (
          <div className="mb-6">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-600 h-2 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            {progressMessage && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {progressMessage}
              </p>
            )}
          </div>
        )}

        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === "success"
                ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200"
                : message.type === "warning"
                ? "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200"
                : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
            }`}
          >
            {message.text}
          </div>
        )}

        {uploadResult && (
          <div className="mb-6">
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-600 border-b border-gray-200 dark:border-gray-500">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Upload Results
                </h3>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {uploadResult.created || 0}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Created
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {uploadResult.skipped || 0}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Skipped (Duplicates)
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {uploadResult.total_errors || 0}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Errors
                    </div>
                  </div>
                </div>

                {typeof uploadResult.total_processed === "number" && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 text-center md:text-left mb-2">
                    Total processed: {uploadResult.total_processed}
                  </div>
                )}

                {uploadResult.summary && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {uploadResult.summary}
                    </p>
                  </div>
                )}

                {downloadUrl && (
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={downloadResults}
                      className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md transition-colors"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download Report
                    </button>
                  </div>
                )}

                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                      Error Details
                    </h4>
                    <div className="max-h-60 overflow-y-auto bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <ul className="space-y-1">
                        {uploadResult.errors.map((error, index) => (
                          <li
                            key={index}
                            className="text-sm text-red-700 dark:text-red-300 font-mono"
                          >
                            {error}
                          </li>
                        ))}
                      </ul>
                      {uploadResult.total_errors >
                        uploadResult.errors.length && (
                        <div className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                          ... and{" "}
                          {uploadResult.total_errors -
                            uploadResult.errors.length}{" "}
                          more errors
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {uploadResult.skipped_details &&
                  uploadResult.skipped_details.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                        Skipped Records Details
                      </h4>
                      <div className="max-h-60 overflow-y-auto bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <ul className="space-y-1">
                          {uploadResult.skipped_details.map((skip, index) => (
                            <li
                              key={index}
                              className="text-sm text-blue-700 dark:text-blue-300 font-mono"
                            >
                              Row {skip.row}: {skip.skip_reason}
                            </li>
                          ))}
                        </ul>
                        {uploadResult.skipped >
                          uploadResult.skipped_details.length && (
                          <div className="mt-2 text-sm text-blue-600 dark:text-blue-400 font-medium">
                            ... and{" "}
                            {uploadResult.skipped -
                              uploadResult.skipped_details.length}{" "}
                            more skipped records
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className={`inline-flex items-center px-6 py-3 font-medium rounded-md transition-colors ${
              !file || isUploading
                ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {isUploading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 mr-2" />
                Upload Intervention Points
              </>
            )}
          </button>
        </div>

        {/* Data Validation Guide Modal */}
        {showValidationGuide && (
          <AddInterventionPointsInfo
            setShowValidationGuide={setShowValidationGuide}
          />
        )}
      </div>
    </div>
  );
}

export default AddInterventionPoints;
